<template>
  <v-container fluid class="email-page">
    <!-- Alert Warning -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" icon="mdi-alert-outline" :icon-size="22" />

    <!-- Send Email Confirm Dialog -->
    <v-dialog v-model="sendEmailDialog" max-width="460" content-class="email-confirm-dialog">
      <div class="email-confirm-card">
        <h3 class="email-confirm-card__title">Send HRDC Email To Client</h3>
        <p class="email-confirm-card__message">Please confirm that you wish to send this email</p>
        <div class="email-confirm-card__actions">
          <button
            class="email-confirm-card__btn email-confirm-card__btn--cancel"
            @click="sendEmailDialog = false"
          >
            Cancel
          </button>
          <button
            class="email-confirm-card__btn email-confirm-card__btn--confirm"
            @click="modalSendHrdcEmailToClient()"
          >
            Submit
          </button>
        </div>
      </div>
    </v-dialog>

    <TrainingDaysListingComponent
      v-if="isHrdcAdminRoles"
      :datas="staticTaxpodData"
    ></TrainingDaysListingComponent>

    <!-- Header -->
    <div class="email-header">
      <div class="email-header__top">

        <div v-if="!loadingDialog" class="email-header__info">
          <h1 class="email-header__title">{{ model.add.name }}</h1>
        </div>

        <div class="email-header__actions">
          <span v-if="!btnDisabled">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="hero-action-btn hero-action-btn--green"
                  :disabled="apiSubmit.isLoading"
                  @click="sendEmailDialog = true"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="18">mdi-check-circle-outline</v-icon>
                </button>
              </template>
              <span>Submit</span>
            </v-tooltip>
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="!loadingDialog" class="cards-grid">
      <!-- LEFT COLUMN -->
      <div class="cards-column">
        <!-- Email Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-email-outline</v-icon>
            <span class="section-header__title">Email Details</span>
          </div>
          <div class="section-body">
            <!-- Email Subject -->
            <div class="form-row">
              <label class="form-label">Email Subject</label>
              <div class="form-field">
                <v-text-field
                  dense
                  filled
                  v-model="formData.emailSubject"
                  :error-messages="errorField.emailSubject"
                  :disabled="inputDisabled"
                ></v-text-field>
              </div>
            </div>

            <!-- TO Recipient -->
            <div class="form-row">
              <label class="form-label">TO Recipient</label>
              <div class="form-field">
                <v-combobox
                  dense
                  filled
                  v-model="formData.toEmail"
                  :error-messages="errorField.toEmail"
                  :hide-selected="true"
                  multiple
                  small-chips
                  :disabled="inputDisabled"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item }}
                      <v-icon small @click="data.parent.selectItem(data.item)">
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple email
                    </v-tooltip>
                  </template></v-combobox
                >
              </div>
            </div>

            <!-- CC Recipient -->
            <div class="form-row">
              <label class="form-label">CC Recipient</label>
              <div class="form-field">
                <v-combobox
                  dense
                  filled
                  v-model="formData.ccEmail"
                  :error-messages="errorField.ccEmail"
                  :hide-selected="true"
                  multiple
                  small-chips
                  :disabled="inputDisabled"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item }}
                      <v-icon small @click="data.parent.selectItem(data.item)">
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple email
                    </v-tooltip>
                  </template></v-combobox
                >
              </div>
            </div>

            <!-- Remarks -->
            <div class="form-row">
              <label class="form-label">Remarks</label>
              <div class="form-field">
                <v-textarea
                  dense
                  filled
                  auto-grow
                  v-model="formData.remarks"
                  :error-messages="errorField.remarks"
                  :disabled="inputDisabled"
                ></v-textarea>
              </div>
            </div>

            <div class="section-divider"></div>

            <!-- Send Email Options -->
            <div class="form-row">
              <label class="form-label">Send Email Options</label>
              <div class="form-field">
                <div class="toggle-card-group">
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-blue': formData.byPassEmailBool === 'true', 'toggle-card--disabled': inputDisabled }"
                    @click="!inputDisabled && (formData.byPassEmailBool = 'true')"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--blue">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Send Email</span>
                      <span class="toggle-card__desc">Email will be sent to recipients</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active': formData.byPassEmailBool === 'true' }">
                      <svg v-if="formData.byPassEmailBool === 'true'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-red': formData.byPassEmailBool === 'false', 'toggle-card--disabled': inputDisabled }"
                    @click="!inputDisabled && (formData.byPassEmailBool = 'false')"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--red">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Do Not Send Email</span>
                      <span class="toggle-card__desc">Skip sending, only save records</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active toggle-card__check--red': formData.byPassEmailBool === 'false' }">
                      <svg v-if="formData.byPassEmailBool === 'false'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Auto Capture Grant Approval Email -->
            <div class="form-row">
              <label class="form-label">Auto Capture Grant Email</label>
              <div class="form-field">
                <div class="toggle-card-group">
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-blue': formData.autoCaptureGrantApprovalEmail === 'true', 'toggle-card--disabled': inputDisabled }"
                    @click="!inputDisabled && (formData.autoCaptureGrantApprovalEmail = 'true')"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--blue">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.745 3.745 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Capture</span>
                      <span class="toggle-card__desc">Auto capture grant approval email</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active': formData.autoCaptureGrantApprovalEmail === 'true' }">
                      <svg v-if="formData.autoCaptureGrantApprovalEmail === 'true'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-red': formData.autoCaptureGrantApprovalEmail === 'false', 'toggle-card--disabled': inputDisabled }"
                    @click="!inputDisabled && (formData.autoCaptureGrantApprovalEmail = 'false')"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--red">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Do Not Capture</span>
                      <span class="toggle-card__desc">Skip auto capturing grant email</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active toggle-card__check--red': formData.autoCaptureGrantApprovalEmail === 'false' }">
                      <svg v-if="formData.autoCaptureGrantApprovalEmail === 'false'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="cards-column">
        <!-- Letter Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-file-document-outline</v-icon>
            <span class="section-header__title">Letter Details</span>
          </div>
          <div class="section-body">
            <!-- Confirmation Letter Company Name -->
            <div class="form-row">
              <label class="form-label">Company Name</label>
              <div class="form-field">
                <v-textarea
                  rows="2"
                  dense
                  filled
                  auto-grow
                  v-model.trim="formData.oriCompanyName"
                  :error-messages="errorField.oriCompanyName"
                  @input="autoEmbedData()"
                  :disabled="inputDisabled"
                ></v-textarea>
              </div>
            </div>

            <!-- Company Address -->
            <div class="form-row">
              <label class="form-label">Company Address</label>
              <div class="form-field">
                <v-textarea
                  rows="4"
                  dense
                  filled
                  auto-grow
                  v-model.trim="formData.companyAddress"
                  :error-messages="errorField.companyAddress"
                  @input="autoEmbedData()"
                  :disabled="inputDisabled"
                ></v-textarea>
              </div>
            </div>

            <!-- Claim Amount -->
            <div class="form-row">
              <label class="form-label">Claim Amount</label>
              <div class="form-field">
                <v-text-field
                  disabled
                  dense
                  filled
                  auto-grow
                  v-model="formData.totalPrice"
                  :error-messages="errorField.totalPrice"
                  @input="autoEmbedData()"
                ></v-text-field>
              </div>
            </div>

            <div class="section-divider"></div>

            <!-- Smart Suggestion: apply-grant deadline (−16 days) is a public holiday -->
            <div v-if="holidaySuggestion" class="smart-suggestion">
              <div class="smart-suggestion__icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.5.4.9 1 1 1.7l.1.5h5l.1-.5c.1-.7.5-1.3 1-1.7A6 6 0 0 0 12 3Z" />
                </svg>
              </div>
              <div class="smart-suggestion__body">
                <span class="smart-suggestion__title">Smart Suggestion</span>
                <span class="smart-suggestion__text">
                  The apply-grant deadline
                  <strong>{{ holidaySuggestion.formatted }}</strong>
                  (16 days before the subscription start) falls on a public holiday —
                  <strong>{{ holidaySuggestion.name }}</strong>. You may want to adjust the subscription date.
                </span>
              </div>
            </div>

            <!-- Subscription Date Range -->
            <div class="form-row">
              <label class="form-label">Subscription Date</label>
              <div class="form-field">
                <!-- Dropdown mode -->
                <div v-if="toggleSubscriptionDate" class="edit-select" v-click-outside="() => subscriptionDropdownOpen = false">
                  <div
                    class="edit-select__trigger"
                    :class="{ 'edit-select__trigger--open': subscriptionDropdownOpen, 'edit-select__trigger--error': errorField.subscriptionDateRange }"
                    @click="subscriptionDropdownOpen = !subscriptionDropdownOpen"
                  >
                    <div class="edit-select__value">
                      <span v-if="formData.subscriptionDateRange && (typeof formData.subscriptionDateRange === 'string' ? formData.subscriptionDateRange : formData.subscriptionDateRange.length > 0)">
                        {{ typeof formData.subscriptionDateRange === 'string' ? formData.subscriptionDateRange : formData.subscriptionDateRange.join(' - ') }}
                      </span>
                      <span v-else class="edit-select__placeholder">Select subscription date...</span>
                    </div>
                    <div class="edit-select__actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <button class="edit-select__mode-btn" @click.stop="toggleSubscriptionDateInput()" v-on="on">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__mode-icon">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                            </svg>
                          </button>
                        </template>
                        <span>Change to calendar view</span>
                      </v-tooltip>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__arrow" :class="{ 'edit-select__arrow--open': subscriptionDropdownOpen }">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </svg>
                    </div>
                  </div>
                  <div v-if="subscriptionDropdownOpen" class="edit-select__dropdown">
                    <div
                      v-for="(item, idx) in dynamicSubscriptionDateItems"
                      :key="'subDate-' + idx"
                      class="edit-select__option"
                      :class="{ 'edit-select__option--selected': formData.subscriptionDateRange === item }"
                      @click="formData.subscriptionDateRange = item; subscriptionDropdownOpen = false; changeSubscriptionDate()"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__option-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                      </svg>
                      {{ item }}
                    </div>
                    <div v-if="dynamicSubscriptionDateItems.length === 0" class="edit-select__empty">
                      No dates available
                    </div>
                  </div>
                  <div v-if="errorField.subscriptionDateRange" class="edit-select__error">
                    <template v-if="Array.isArray(errorField.subscriptionDateRange)">
                      <div v-for="(msg, idx) in errorField.subscriptionDateRange" :key="'subDateErr-' + idx" class="edit-select__error-item">{{ msg }}</div>
                    </template>
                    <template v-else>{{ errorField.subscriptionDateRange }}</template>
                  </div>
                  <div v-if="displayApplyGrantHint && applyGrantHint" class="edit-select__hint">{{ applyGrantHint }}</div>
                </div>

                <!-- Calendar mode -->
                <div v-if="!inputDisabled && !toggleSubscriptionDate" class="edit-select">
                  <v-menu
                    ref="subscriptionMenu"
                    v-model="formData.subscriptionMenu"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="edit-select__trigger"
                        :class="{ 'edit-select__trigger--open': formData.subscriptionMenu, 'edit-select__trigger--error': errorField.subscriptionDateRange }"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <div class="edit-select__value">
                          <span v-if="subscriptionDate && subscriptionDate.length > 0">{{ typeof subscriptionDate === 'string' ? subscriptionDate : subscriptionDate.join(' - ') }}</span>
                          <span v-else class="edit-select__placeholder">Select date range...</span>
                        </div>
                        <div class="edit-select__actions">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on: tooltipOn }">
                              <button class="edit-select__mode-btn" @click.stop="toggleSubscriptionDateInput()" v-on="tooltipOn">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__mode-icon">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                                </svg>
                              </button>
                            </template>
                            <span>Change to option view</span>
                          </v-tooltip>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__arrow" :class="{ 'edit-select__arrow--open': formData.subscriptionMenu }">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                          </svg>
                        </div>
                      </div>
                    </template>
                    <v-date-picker
                      @change="autoEmbedData()"
                      range
                      v-model="formData.subscriptionDateRange"
                      no-title
                      scrollable
                      color="var(--app-accent)"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="formData.subscriptionMenu = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.subscriptionMenu.save(date)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                  <div v-if="errorField.subscriptionDateRange" class="edit-select__error">
                    <template v-if="Array.isArray(errorField.subscriptionDateRange)">
                      <div v-for="(msg, idx) in errorField.subscriptionDateRange" :key="'subDateErr2-' + idx" class="edit-select__error-item">{{ msg }}</div>
                    </template>
                    <template v-else>{{ errorField.subscriptionDateRange }}</template>
                  </div>
                </div>

                <!-- Disabled mode (inputDisabled) -->
                <div v-if="inputDisabled" class="edit-select" v-click-outside="() => subscriptionDropdownOpen = false">
                  <div
                    class="edit-select__trigger"
                    :class="{ 'edit-select__trigger--open': subscriptionDropdownOpen, 'edit-select__trigger--error': errorField.subscriptionDateRange }"
                    @click="subscriptionDropdownOpen = !subscriptionDropdownOpen"
                  >
                    <div class="edit-select__value">
                      <span v-if="formData.subscriptionDateRange && (typeof formData.subscriptionDateRange === 'string' ? formData.subscriptionDateRange : formData.subscriptionDateRange.length > 0)">
                        {{ typeof formData.subscriptionDateRange === 'string' ? formData.subscriptionDateRange : formData.subscriptionDateRange.join(' - ') }}
                      </span>
                      <span v-else class="edit-select__placeholder">Select subscription date...</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__arrow" :class="{ 'edit-select__arrow--open': subscriptionDropdownOpen }">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                  <div v-if="subscriptionDropdownOpen" class="edit-select__dropdown">
                    <div
                      v-for="(item, idx) in dynamicSubscriptionDateItems"
                      :key="'subDateDisabled-' + idx"
                      class="edit-select__option"
                      :class="{ 'edit-select__option--selected': formData.subscriptionDateRange === item }"
                      @click="formData.subscriptionDateRange = item; subscriptionDropdownOpen = false; changeSubscriptionDate()"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="edit-select__option-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                      </svg>
                      {{ item }}
                    </div>
                  </div>
                  <div v-if="errorField.subscriptionDateRange" class="edit-select__error">
                    <template v-if="Array.isArray(errorField.subscriptionDateRange)">
                      <div v-for="(msg, idx) in errorField.subscriptionDateRange" :key="'subDateErr3-' + idx" class="edit-select__error-item">{{ msg }}</div>
                    </template>
                    <template v-else>{{ errorField.subscriptionDateRange }}</template>
                  </div>
                  <div v-if="displayApplyGrantHint && applyGrantHint" class="edit-select__hint">{{ applyGrantHint }}</div>
                </div>
              </div>
            </div>

            <!-- JD14 Font Size -->
            <div class="form-row">
              <label class="form-label">JD14 Font Size</label>
              <div class="form-field">
                <v-text-field
                  type="number"
                  dense
                  filled
                  label="Company Name & Address Font Size"
                  v-model="formData.companyAddressFontSize"
                  :error-messages="errorField.companyAddressFontSize"
                  @change="autoEmbedData()"
                  :disabled="inputDisabled"
                ></v-text-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Attachments -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-paperclip</v-icon>
            <span class="section-header__title">Attachments</span>
          </div>
          <div class="section-body">
            <!-- Confirmation Letter -->
            <div class="form-row">
              <label class="form-label">Confirmation Letter</label>
              <div class="form-field">
                <div
                  class="file-card"
                  :class="formData.confirmationLetterUrl ? 'file-card--has-file' : 'file-card--empty'"
                  @drop.prevent="addDropFile($event, 'confirmationLetterUrl')"
                  @dragover.prevent
                >
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.confirmationLetterUrl ? formData.confirmationLetterUrl.name : 'No file selected' }}</span>
                    <span class="file-card__hint">PDF only</span>
                  </div>
                  <div class="file-card__actions">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <label class="file-card__btn" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                          <input type="file" class="file-card__hidden" accept=".pdf" @change="handleEmailFileChange($event, 'confirmationLetterUrl')" :disabled="inputDisabled" />
                        </label>
                      </template>
                      <span>Upload</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <button class="file-card__btn" @click="previewAutoEmbedPdf('confirmationLetterUrl')" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                        </button>
                      </template>
                      <span>Preview</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="errorField.confirmationLetterUrl" class="file-card__error">{{ Array.isArray(errorField.confirmationLetterUrl) ? errorField.confirmationLetterUrl.join(', ') : errorField.confirmationLetterUrl }}</div>
              </div>
            </div>

            <!-- JD14 Form -->
            <div class="form-row">
              <label class="form-label">JD14 Form</label>
              <div class="form-field">
                <div
                  class="file-card"
                  :class="formData.sendJd14Url ? 'file-card--has-file' : 'file-card--empty'"
                  @drop.prevent="addDropFile($event, 'sendJd14Url')"
                  @dragover.prevent
                >
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.sendJd14Url ? formData.sendJd14Url.name : 'No file selected' }}</span>
                    <span class="file-card__hint">PDF only</span>
                  </div>
                  <div class="file-card__actions">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <label class="file-card__btn" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                          <input type="file" class="file-card__hidden" accept=".pdf" @change="handleEmailFileChange($event, 'sendJd14Url')" :disabled="inputDisabled" />
                        </label>
                      </template>
                      <span>Upload</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <button class="file-card__btn" @click="previewAutoEmbedPdf('sendJd14Url')" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                        </button>
                      </template>
                      <span>Preview</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="errorField.sendJd14Url" class="file-card__error">{{ Array.isArray(errorField.sendJd14Url) ? errorField.sendJd14Url.join(', ') : errorField.sendJd14Url }}</div>
              </div>
            </div>

            <!-- T3 Form -->
            <div class="form-row">
              <label class="form-label">T3 Form</label>
              <div class="form-field">
                <div
                  class="file-card"
                  :class="formData.sendT3Url ? 'file-card--has-file' : 'file-card--empty'"
                  @drop.prevent="addDropFile($event, 'sendT3Url')"
                  @dragover.prevent
                >
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.sendT3Url ? formData.sendT3Url.name : 'No file selected' }}</span>
                    <span class="file-card__hint">PDF only</span>
                  </div>
                  <div class="file-card__actions">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <label class="file-card__btn" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                          <input type="file" class="file-card__hidden" accept=".pdf" @change="handleEmailFileChange($event, 'sendT3Url')" :disabled="inputDisabled" />
                        </label>
                      </template>
                      <span>Upload</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <button class="file-card__btn" @click="previewAutoEmbedPdf('sendT3Url')" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                        </button>
                      </template>
                      <span>Preview</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="errorField.sendT3Url" class="file-card__error">{{ Array.isArray(errorField.sendT3Url) ? errorField.sendT3Url.join(', ') : errorField.sendT3Url }}</div>
              </div>
            </div>

            <div class="section-divider"></div>

            <!-- Auto-included files (read-only) -->
            <div class="form-row">
              <label class="form-label">Auto-included</label>
              <div class="form-field">
                <div class="file-card file-card--has-file file-card--readonly">
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.flyer ? formData.flyer.name : 'Flyer' }}</span>
                    <span class="file-card__hint file-card__hint--auto">Auto-attached</span>
                  </div>
                </div>
                <div class="file-card file-card--has-file file-card--readonly" style="margin-top: 8px;">
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.guide ? formData.guide.name : 'HRDC Claim Application Guideline' }}</span>
                    <span class="file-card__hint file-card__hint--auto">Auto-attached</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Preview FAB -->
    <button v-if="!loadingDialog" class="preview-fab" :class="{ 'preview-fab--shine': showPreviewShine }" @click="emailPreviewOpen = true">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="preview-fab__icon">
        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
      <span class="preview-fab__label">Preview</span>
    </button>

    <!-- Email Preview Modal -->
    <transition name="modal">
      <div v-if="emailPreviewOpen" class="email-modal-overlay" @click.self="emailPreviewOpen = false">
        <div class="email-modal">
          <!-- Modal Header -->
          <div class="email-modal__header">
            <div class="email-modal__header-left">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="email-modal__header-icon">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <span>Email Preview</span>
            </div>
            <button class="email-modal__close" @click="emailPreviewOpen = false">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="email-modal__body">
            <!-- Email Metadata -->
            <div class="email-preview__meta">
              <div class="email-preview__meta-row">
                <span class="email-preview__meta-label">Subject:</span>
                <span class="email-preview__meta-value">{{ formData.emailSubject || 'TAXPOD MASTERCLASS HRDC GRANT APPLICATION' }}</span>
              </div>
              <div class="email-preview__meta-row">
                <span class="email-preview__meta-label">To:</span>
                <span class="email-preview__meta-value">{{ formData.toEmail ? (Array.isArray(formData.toEmail) ? formData.toEmail.join('; ') : formData.toEmail) : 'recipient@example.com' }}</span>
              </div>
              <div v-if="formData.ccEmail && (Array.isArray(formData.ccEmail) ? formData.ccEmail.length > 0 : formData.ccEmail)" class="email-preview__meta-row">
                <span class="email-preview__meta-label">Cc:</span>
                <span class="email-preview__meta-value">{{ Array.isArray(formData.ccEmail) ? formData.ccEmail.join('; ') : formData.ccEmail }}</span>
              </div>
            </div>

            <!-- Email Body -->
            <div class="email-preview__content">
            <div class="email-preview__sheet">
            <div class="email-preview__body">
              <p>Dear Valued Customer,</p>
              <p>Thank you for signing up taxPOD! Here's how to proceed with your HRDC application:</p>

              <p class="email-preview__step">Step 1: Apply HRDC Grant</p>
              <p>
                Submit your grant application through the HRDC eTRIS system
                (<a href="https://etris.hrdcorp.gov.my/DigiGov/login.jsp" target="_blank" rel="noopener">https://etris.hrdcorp.gov.my/DigiGov/login.jsp</a>)
                <span v-if="applyGrantHint">latest by <span class="email-preview__highlight">{{ applyGrantHint }}</span></span>
                to avoid expiry of your Confirmation Letter.
              </p>

              <p>Kindly refer to the attached guide,</p>

              <ol class="email-preview__list">
                <li>
                  YYC taxPOD <strong>HRDC Grant Application Procedure</strong>
                  <br />
                  <span class="email-preview__note">*Note: Register only one trainee name per taxPOD account</span>
                  <br />
                  <span class="email-preview__note">*Use the same trainee details in T3 Form</span>
                </li>
                <li>
                  Use the attachments <strong>as supporting documents:</strong>
                  <ol class="email-preview__list email-preview__list--alpha">
                    <li>"Confirmation Letter (taxPOD)"</li>
                    <li>"taxPOD Masterclass Flyer"</li>
                  </ol>
                </li>
              </ol>

              <p class="email-preview__step">Step 2: Get HRDC Approval</p>
              <ul class="email-preview__list">
                <li>After submission, check your eTRIS account for any HRDC query.</li>
                <li>Once approved, you'll receive an approval letter from HRDC in eTRIS and your mailbox.</li>
              </ul>

              <p class="email-preview__step">Step 3: Return 3 completed documents to this email:</p>
              <ol class="email-preview__list">
                <li>Grant approval letter</li>
                <li>
                  Filled JD14 Form
                  <br />
                  <span class="email-preview__note">*Note: Enter <u>the employer code</u> and <u>grant approval number</u> in Part 1</span>
                </li>
                <li>
                  Filled T3 Form
                  <br />
                  <span class="email-preview__note">*Note: <u>Fill up all details</u> &amp; <strong><em>DO NOT SIGN</em></strong></span>
                </li>
              </ol>

              <p>If you require any assistance, please contact us at <a href="mailto:hrdc.gst@yycadvisors.com">hrdc.gst@yycadvisors.com</a>.</p>

              <div class="email-preview__signature">
                <p>Best Regards,</p>
                <p><strong>HRDC Support | YYC taxPOD</strong></p>
                <p>YYC GST Consultants Sdn Bhd (1073010-M)</p>
              </div>
            </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import TrainingDaysListingComponent from "@/components/views/TrainingDaysListingComponent";

export default {
  mixins: [hrdcFunction],
  components: {
    TrainingDaysListingComponent,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    showPreviewShine() {
      var launch = new Date(this.previewFeatureLaunchDate);
      var now = new Date();
      var diff = (now - launch) / (1000 * 60 * 60 * 24);
      return diff < 3;
    },
    subscriptionDate() {
      if (typeof this.formData.subscriptionDateRange !== "string") {
        return this.formData.subscriptionDateRange.join(" - ");
      }

      return [];
    },
    inputDisabled() {
      if (this.isApplicationSendBefore && this.isSalesManagerRoles) {
        return true;
      }

      return (
        this.isApplicationSendBefore &&
        this.isSalespersonRoles &&
        this.auth.roles.length == 1
      );
    },
  },
  props: ["params"],
  data: () => ({
    apiSubmit: new Api(),
    apiScanPdfText: new Api(),
    apiGetHrdcEmailDetails: new Api(),
    apiAutoEmbedData: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    stages: null,
    alertError: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    formData: {
      applicationNo: null,
      toEmail: [],
      ccEmail: [],
      emailSubject: null,
      subscriptionDateRange: [],
      confirmationLetterUrl: null,
      sendJd14Url: null,
      sendT3Url: null,
      quotationMenu: false,
      subscriptionMenu: false,
      remarks: null,
      byPassEmailBool: "true",
      companyAddress: null,
      companyAddressFontSize: 10,
      oriCompanyName: null,
      autoCaptureGrantApprovalEmail: "true",
      claimAmount: null,
      programmeName: null,
    },
    errorField: {
      applicationNo: null,
      toEmail: null,
      ccEmail: null,
      emailSubject: null,
      subscriptionDateRange: null,
      confirmationLetterUrl: null,
      sendJd14Url: null,
      sendT3Url: null,
      quotationMenu: null,
      subscriptionMenu: null,
      companyAddressFontSize: null,
      autoCaptureGrantApprovalEmail: null,
    },
    loadingDialog: false,
    sendEmailDialog: false,
    emailPreviewOpen: false,
    previewFeatureLaunchDate: '2026-03-19',
    btnDisabled: false,
    editMode: false,
    isApplicationSendBefore: false,
    dynamicSubscriptionDateItems: [],
    totalEventDays: null,
    displayApplyGrantHint: false,
    applyGrantHint: null,
    // Smart Suggestion: set when the apply-grant deadline (−16 days) is a
    // public holiday in KUL. Display-only; never blocks sending.
    holidaySuggestion: null,
    lastHolidayCheckYmd: null,
    toggleSubscriptionDate: true,
    nextStageId: null,
    subscriptionDropdownOpen: false,
  }),
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/email_client/create-email`,
        method: "post",
      },
      apiScanPdfText: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/email_client/scan-pdf`,
        method: "post",
      },
      apiGetHrdcEmailDetails: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/email_client/get-email-details`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiAutoEmbedData: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/email_client/auto-embed-data`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state.hrdc.data.stageId];

    this.rolesAssignation(this.auth.roles);

    this.checkFormAlreadyFilledGte(
      currentStageId,
      this.hrdcData.stageSlug.grantApprovalNoSubmitted
    );

    this.checkFormNotYetFilledLesser(
      currentStageId,
      this.hrdcData.stageSlug.applicationApproved,
      "Application haven't approve by taxPOD Admin"
    );

    // Get email data.
    this.apiGetHrdcEmailDetails.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;

        // In Edit Mode.
        if (status == true) {
          data.companyAddress = response.companyAddress;
          this.formData = this.transformEmailDetails(data);
          this.isApplicationSendBefore = true;

          this.hideLoadingDialog();
        } else {
          // First time sending.
          // Auto assigned recipient fields.
          // Split companyPicDetail by recipientType. Legacy rows (null) are
          // treated as TO so pre-migration applications still populate sensibly.
          const picTo = response.companyPicDetail
            .filter(
              (item) =>
                item.picEmail &&
                (item.recipientType === "TO" || item.recipientType == null)
            )
            .map((item) => item.picEmail);
          const picCc = response.companyPicDetail
            .filter((item) => item.picEmail && item.recipientType === "CC")
            .map((item) => item.picEmail);

          const toEmailArr = [...picTo];
          const ccEmailArr = [data.referByEmail, ...response.csData, ...picCc];

          this.formData.toEmail = toEmailArr.filter(
            (item, index, self) => item !== null && self.indexOf(item) == index
          );
          this.formData.ccEmail = ccEmailArr.filter(
            (item, index, self) => item !== null && self.indexOf(item) == index
          );
          this.formData.oriCompanyName = data.oriCompanyName;
          this.formData.emailSubject =
            `taxPOD Masterclass HRDC Grant Application - ${this.capitalizeFirstLetter(
              data.oriCompanyName
            )}`.toUpperCase();

          this.formData.companyAddress = response.companyAddress;
          this.formData.companyAddressFontSize = 10;
        }

        let version = "3.1";
        let basePrice = null;
        basePrice = this.getTaxpodBasePricing(data.totalPrice);
        if (basePrice) {
          version = this.staticTaxpodData.find(
            (item) => item.price == basePrice
          ).flyer;
        }

        this.formData.flyer = {
          name: `taxPOD Masterclass Flyer ${version}.pdf`.trim(),
        };

        this.formData.guide = {
          name: `YYC taxPOD HRDC Grant and Claim Application Guideline ${version}.pdf`.trim(),
        };

        // Append claim amount.
        this.formData.claimAmount = data.totalPrice;
        this.formData.totalPrice =
          "RM " + Number(data.totalPrice).toLocaleString();

        // Append programme name.
        this.formData.programmeName = data.programmeName;

        if (!this.isHrdcAdminRoles) {
          this.alertError.push("You are not allowed to perform this action!");
          this.btnDisabled = true;
        }

        // Check grant email existing or not.
        const isHrdcGrantEmailExist = data.ApplicationAttachment.find(
          (item) => item.stageId == this.hrdcData.stagePriority.hrdcGrantEmail
        );

        /*
          Conditions for taxPOD salesperson to resend email to client on their own:
          1. Salesperson Role
          2. Email is being sent before (2nd time onwards)
          3. Grant is incomplete (red cross)
        */
        // Use fresh stageId from API response instead of potentially stale Vuex store value.
        const currentStageId = data.stageId ?? this.$store.state.hrdc.data.stageId;
        const isSalesRole = this.isSalespersonRoles || this.isSalesManagerRoles;
        const isApplicationApproved =
          currentStageId === this.hrdcData.stagePriority.applicationApproved;

        // Handle salesperson/sales manager roles and application approval
        if (
          isSalesRole &&
          this.isApplicationSendBefore &&
          !data.grantApprovalNo
        ) {
          this.toggleSubscriptionDate = false;
          const stageIsSentConfirmation =
            currentStageId ===
            this.hrdcData.stagePriority.sentConfirmationEmailToClient;

          if (
            (!isHrdcGrantEmailExist && !data.revertGrantStatus) ||
            stageIsSentConfirmation
          ) {
            this.alertError = [];
            this.btnDisabled = false;
            this.editMode = false;
          }
        }

        // Manage edit mode based on stage ID
        if (currentStageId < this.hrdcData.stageSlug.clientRevertT3Form) {
          this.editMode = false;
        }

        // Set the next stage ID
        this.nextStageId = isApplicationApproved
          ? this.hrdcData.stagePriority.sentConfirmationEmailToClient
          : currentStageId;

        // Toggle subscription date view for sales roles
        this.toggleSubscriptionDate = !(
          isSalesRole && !this.isCustomerServiceAdminRoles
        );

        // Assign dynamic subscription period to allow TBOs select.
        this.dynamicSubscriptionDateItems =
          this.generateDynamicSubscriptionDate();

        // If status is new, continue to the normal flow.
        if (status == "new") {
          this.hideLoadingDialog();
        }
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetHrdcEmailDetails.fetch();
  },
  watch: {
    // Re-check the apply-grant deadline holiday whenever the subscription date
    // changes (covers dropdown, calendar range, and prefill-on-edit).
    "formData.subscriptionDateRange": {
      handler(val) {
        this.checkApplyGrantHoliday(this.extractSubscriptionStart(val));
      },
    },
  },
  methods: {
    // Pull the subscription START date (YYYY-MM-DD) out of either the array
    // form (["2026-05-01","2026-05-10"]) or the string form ("... - ...").
    extractSubscriptionStart(val) {
      if (!val) return null;
      if (Array.isArray(val)) return val[0] || null;
      if (typeof val === "string") return val.split(" - ")[0] || null;
      return null;
    },
    // Smart Suggestion — ask the Malaysia holiday API (host + state come from
    // VUE_APP_HOLIDAY_* in .env) whether the apply-grant deadline (16 days
    // before the subscription start) is a public holiday, and surface it to the
    // admin. Purely informational.
    async checkApplyGrantHoliday(subscriptionStartYmd) {
      if (!subscriptionStartYmd) {
        this.holidaySuggestion = null;
        this.lastHolidayCheckYmd = null;
        return;
      }

      const deadline = moment(subscriptionStartYmd, "YYYY-MM-DD").subtract(
        16,
        "days"
      );
      if (!deadline.isValid()) {
        this.holidaySuggestion = null;
        return;
      }

      const ymd = deadline.format("YYYY-MM-DD");
      // Skip a redundant request when the computed deadline hasn't changed.
      if (ymd === this.lastHolidayCheckYmd) return;
      this.lastHolidayCheckYmd = ymd;

      // API host + state are configurable via .env (VUE_APP_HOLIDAY_*).
      const holidayApiUrl = process.env.VUE_APP_HOLIDAY_API_URL;
      const holidayState = process.env.VUE_APP_HOLIDAY_STATE;
      if (!holidayApiUrl || !holidayState) {
        this.holidaySuggestion = null;
        return;
      }

      try {
        const res = await fetch(
          `${holidayApiUrl}/holidays/check?date=${ymd}&state=${holidayState}`
        );
        const data = await res.json();

        // Ignore stale responses if the user changed the date meanwhile.
        if (ymd !== this.lastHolidayCheckYmd) return;

        if (data && data.is_holiday && Array.isArray(data.holidays) && data.holidays.length) {
          this.holidaySuggestion = {
            date: ymd,
            formatted: deadline.format("D MMM YYYY"),
            name: data.holidays.map((h) => h.name).join(", "),
          };
        } else {
          this.holidaySuggestion = null;
        }
      } catch (err) {
        // Non-blocking: a holiday-API hiccup must never stop the admin working.
        console.log("[email_client] holiday check failed:", err);
        this.holidaySuggestion = null;
      }
    },
    // Drag & Drop file function.
    handleEmailFileChange(event, key) {
      const file = event.target.files[0];
      if (file) {
        this.formData[key] = file;
        this.autoEmbedData();
      }
    },
    addDropFile(e, key) {
      let file = e.dataTransfer.files[0];

      if (!file.type.match("pdf.*")) {
        this.errorField[key] = "Please select pdf file format";
        return;
      }

      this.formData[key] = file;

      switch (key) {
        case "confirmationLetterUrl":
          this.scanPdf(true);
          break;

        default:
          break;
      }
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    autoEmbedData(manualSubscriptionDateRange = null) {
      // Get the selected training start & end date.
      let trainingStartDate, trainingEndDate;
      if (manualSubscriptionDateRange) {
        [trainingStartDate, trainingEndDate] = manualSubscriptionDateRange;
      } else {
        let trainingDate = this.formData.subscriptionDateRange;
        if (typeof trainingDate == "string") {
          trainingDate = trainingDate.split(" - ");
          [trainingStartDate, trainingEndDate] = trainingDate;
        } else {
          [trainingStartDate, trainingEndDate] =
            this.formData.subscriptionDateRange;
        }
      }

      if (trainingStartDate > trainingEndDate) {
        const dateErrorMsg = "Start Date cannot greater than End Date";
        this.errorField.subscriptionDateRange = dateErrorMsg;
        return;
      }

      this.errorField.subscriptionDateRange = null;

      const basePrice = this.getTaxpodBasePricing(this.formData.claimAmount);
      const staticTaxpodObj = this.staticTaxpodData.find(
        (item) => item.price == basePrice
      );
      let programmeName = "TaxPOD Masterclass";
      if (staticTaxpodObj.flyer) {
        programmeName += ` ${staticTaxpodObj.flyer}`;
      }
      this.formData.programmeName = programmeName;

      this.apiAutoEmbedData.setParams({
        applicationUuid: this.$route.query._ausk,
        trainingStartDate,
        trainingEndDate,
        companyAddress: this.formData.companyAddress,
        companyAddressFontSize: this.formData.companyAddressFontSize,
        confirmationLetterCompanyName: this.formData.oriCompanyName,
        programmeName,
      });

      this.apiAutoEmbedData.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;

          if (!status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "error",
              timeout: 2000,
            });

            this.formData.confirmationLetterUrl = null;
            this.formData.sendT3Url = null;
            this.formData.sendJd14Url = null;
            return;
          }

          if (status) {
            for (const file of response.data) {
              let blob = null;
              for (const item in file) {
                const fileItem = file[item];

                blob = new Blob([new Uint8Array(fileItem)]);

                switch (item) {
                  case "jd14":
                    this.formData.sendJd14Url = new File([blob], `JD14.pdf`, {
                      type: "application/pdf",
                    });
                    break;
                  case "t3":
                    this.formData.sendT3Url = new File([blob], `T3.pdf`, {
                      type: "application/pdf",
                    });
                    break;
                  case "confirmationLetter":
                    this.formData.confirmationLetterUrl = new File(
                      [blob],
                      `Confirmation Letter.pdf`,
                      {
                        type: "application/pdf",
                      }
                    );
                    break;
                }
              }
            }
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });

      if (
        this.formData.companyAddress &&
        this.formData.oriCompanyName &&
        trainingStartDate &&
        trainingEndDate
      ) {
        this.showLoadingDialog();
        this.apiAutoEmbedData.fetch();
      }
    },
    transformEmailDetails(data) {
      const emailData = {
        toEmail: [],
        ccEmail: [],
      };

      const { ApplicationAttachment, ApplicationEmail, ApplicationLog } = data;

      const ApplicationLogSubscriptionDateScheduledObj = ApplicationLog.find(
        (item) =>
          item.stageId == this.hrdcData.stagePriority.subscriptionDateScheduled
      );

      const ApplicationLogSentEmailConfirmationObj = ApplicationLog.find(
        (item) =>
          item.stageId ==
          this.hrdcData.stagePriority.sentConfirmationEmailToClient
      );

      const ApplicationEmailObj = ApplicationEmail.find(
        (item) =>
          item.stageId ==
          this.hrdcData.stagePriority.sentConfirmationEmailToClient
      );

      emailData.remarks =
        ApplicationLogSentEmailConfirmationObj.remarks == "null"
          ? null
          : ApplicationLogSentEmailConfirmationObj.remarks;

      // Get Email Recipient (replace with what admin has updated).
      // Split companyPicDetail by recipientType. Legacy rows (null) → TO.
      const picTo = data.companyPicDetail
        .filter(
          (item) =>
            item.picEmail &&
            (item.recipientType === "TO" || item.recipientType == null)
        )
        .map((item) => item.picEmail);
      const picCc = data.companyPicDetail
        .filter((item) => item.picEmail && item.recipientType === "CC")
        .map((item) => item.picEmail);

      const toEmailArr = [...picTo];
      const ccEmailArr = [data.referByEmail, ...data.csData, ...picCc];

      this.formData.toEmail = toEmailArr.filter(
        (item, index, self) => item !== null && self.indexOf(item) == index
      );

      this.formData.ccEmail = ccEmailArr.filter(
        (item, index, self) => item !== null && self.indexOf(item) == index
      );

      for (const email of this.formData.toEmail) {
        emailData.toEmail.push(email);
      }

      for (const email of this.formData.ccEmail) {
        emailData.ccEmail.push(email);
      }

      // Assign email subject.
      emailData.emailSubject = ApplicationEmailObj.emailSubject.toUpperCase();

      // Assign company address & font size.
      emailData.companyAddress = data.companyAddress;
      emailData.companyAddressFontSize = 10;

      //  Assign course start & end date.
      const courseStartDate =
        ApplicationLogSubscriptionDateScheduledObj.startDate;
      const courseEndDate = ApplicationLogSubscriptionDateScheduledObj.endDate;

      emailData.subscriptionDateRange = [
        moment(courseStartDate, "YYYY-MM-DD HH:mm:ssZ").format("YYYY-MM-DD"),
        moment(courseEndDate, "YYYY-MM-DD HH:mm:ssZ").format("YYYY-MM-DD"),
      ];

      // Assign Send Email byPass value to true.
      emailData.byPassEmailBool = "true";

      // Assign auto capture grant approval email.
      emailData.autoCaptureGrantApprovalEmail = data.grantEmailHook
        ? "true"
        : "false";

      // Assign Company name to confirmation letter.
      emailData.oriCompanyName = data.oriCompanyName;

      // Assign attachment.
      for (const item of ApplicationAttachment) {
        // Create a Blob object from the data
        const blob = new Blob([new Uint8Array(item.dataArray)]);

        // Create the File object
        const file = new File([blob], item.fileName, {
          type: "application/pdf",
        });

        switch (item.type) {
          case "JD14":
            emailData.sendJd14Url = file;
            break;
          case "T3":
            emailData.sendT3Url = file;
            break;
          case "Confirmation Email":
            emailData.confirmationLetterUrl = file;
            break;
        }
      }

      return emailData;
    },
    scanPdf($event) {
      if ($event) {
        this.showLoadingDialog();
        const fd = new FormData();
        fd.append("confirmationLetterUrl", this.formData.confirmationLetterUrl);

        this.apiScanPdfText.setParams(fd);
        this.apiScanPdfText.setCallbackCompleted((response) => {
          try {
            if (response.status) {
              // Split the original date range using the hyphen as a delimiter
              const dateParts = response.data.split(/[\s\u2013-]+/);

              // Format the date parts into the desired format
              const startDateParts = dateParts[0].trim().split("/");
              const endDateParts = dateParts[1].trim().split("/");

              const startDate = `${startDateParts[2]}-${startDateParts[1]}-${startDateParts[0]}`;
              const endDate = `${endDateParts[2]}-${endDateParts[1]}-${endDateParts[0]}`;

              // Construct the new formatted date range
              const formattedDateRange = [startDate, endDate];
              this.formData.subscriptionDateRange = formattedDateRange;
            } else {
              this.$store.dispatch("showMessage", {
                message: response.data,
                messageType: "error",
                timeout: 2000,
              });
            }
            this.hideLoadingDialog();
          } catch (err) {
            console.log(err);
          }
        });
        this.apiScanPdfText.fetch();
      }
    },
    previewAutoEmbedPdf(fileType) {
      if (!this.formData[fileType]) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
        });
        return;
      }

      let url = null;
      let newWindow = null;
      switch (fileType) {
        default:
          url = URL.createObjectURL(this.formData[fileType]);
          newWindow = window.open(url, "_blank");
          break;
      }

      if (newWindow) {
        newWindow.onload = function () {
          // Revoke the object URL when the window is closed
          newWindow.addEventListener("beforeunload", function () {
            URL.revokeObjectURL(url);
          });
        };
      }
    },
    toggleSubscriptionDateInput() {
      this.applyGrantHint = null;
      this.formData.subscriptionDateRange = [];
      this.toggleSubscriptionDate = !this.toggleSubscriptionDate;
    },
    capitalizeFirstLetter(str) {
      return str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    modalSendHrdcEmailToClient() {
      this.sendEmailDialog = !this.sendEmailDialog;
      this.submit();
    },
    generateDynamicSubscriptionDate() {
      // Get the total days need to assign.
      const daysToCount = this.priceToDays();

      const result = [];
      const oneDay = 24 * 60 * 60 * 1000;
      const now = new Date();
      const formattedDate = now.toISOString().split("T")[0];
      let validDate = new Date(formattedDate);
      // Earliest selectable subscription start is 16 days out: the client must
      // apply for the HRDC grant at least 16 days before the start date (see
      // formatLatestValidApplyGrantDate, which subtracts 16), so an earlier
      // start would put the apply-grant deadline in the past.
      validDate.setDate(validDate.getDate() + 16);
      let loopCount = 0;
      let iterations = 12;

      while (loopCount < iterations) {
        let rangeEnd = new Date(validDate);
        // let isSundayInRange = false;

        // Find the range end date, checking for Sundays
        for (let i = 0; i < daysToCount - 1; i++) {
          rangeEnd = new Date(rangeEnd.getTime() + oneDay);
          // if (rangeEnd.getDay() === 0) {
            // 0 is Sunday
            // isSundayInRange = true;
            // break;
          // }
        }

        // Skip this range if it contains a Sunday
        // if (!isSundayInRange) {
          const formattedStart = validDate.toISOString().split("T")[0];
          const formattedEnd = rangeEnd.toISOString().split("T")[0];

          result.push(`${formattedStart} - ${formattedEnd}`);
          loopCount++;
        // }

        // Move to the next day
        validDate = new Date(validDate.getTime() + oneDay);
        // if (validDate.getDay() === 0) {
        //   // Skip Sunday
        //   validDate = new Date(validDate.getTime() + oneDay);
        // }
      }

      return result;
    },
    changeSubscriptionDate() {
      if (this.formData.subscriptionDateRange) {
        this.displayApplyGrantHint = true;
      }

      const dateArr = this.formData.subscriptionDateRange.split(" - ");
      this.applyGrantHint = `*Apply grant latest by ${this.formatLatestValidApplyGrantDate(
        dateArr[0]
      )}`;
      this.formData.subscriptionDateRange = `${dateArr[0]} - ${dateArr[1]}`;

      this.autoEmbedData(dateArr);
    },
    priceToDays() {
      const basePrice = this.getTaxpodBasePricing(this.formData.claimAmount);
      const staticDataObj = this.staticTaxpodData.find(
        (item) => item.price == basePrice
      );
      return staticDataObj.trainingDays;
    },
    formatLatestValidApplyGrantDate(date) {
      let dateObj = new Date(date);

      // Subtract 16 days — client must apply grant at least 16 days before
      // the subscription start date (kept in sync with the backend email).
      dateObj.setDate(dateObj.getDate() - 16);

      // Format the date as "10 Sept 2024"
      const options = { day: "numeric", month: "short", year: "numeric" };
      return dateObj.toLocaleDateString("en-GB", options);
    },
    convertSubscriptionDateRange(dateRange) {
      if (dateRange) {
        let formatToCheck = " - ";

        // If found index of ' - ', then split it to comma format.
        if (dateRange.indexOf(formatToCheck) !== -1) {
          let dateRangeSplitArr = dateRange.split(formatToCheck);
          return `${dateRangeSplitArr[0]},${dateRangeSplitArr[1]}`;
        }
      }
      return dateRange;
    },
    redirectBack() {
      let route = "HrdcApplication";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state.hrdc.data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    async submit() {
      this.showLoadingDialog();
      // Generate Form Data to handle multipart/form-data
      const fd = new FormData();
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("toEmail", this.formData.toEmail);
      fd.append("ccEmail", this.formData.ccEmail);
      fd.append("emailSubject", this.formData.emailSubject);

      // Subscription Date checking.
      let subscriptionDate = this.formData.subscriptionDateRange;
      if (this.inputDisabled) {
        // If TBOs resend, ensure must fill in before send.
        // Check whether the selected date is inside of dynamic generate date range or not.
        if (!this.dynamicSubscriptionDateItems.includes(subscriptionDate)) {
          subscriptionDate = null;
        }
      }

      fd.append(
        "subscriptionDateRange",
        this.convertSubscriptionDateRange(subscriptionDate)
      );
      fd.append("confirmationLetterUrl", this.formData.confirmationLetterUrl);
      fd.append("sendJd14Url", this.formData.sendJd14Url);
      fd.append("sendT3Url", this.formData.sendT3Url);
      fd.append("referById", this.auth.uuid);
      fd.append("referByName", this.auth.name);
      fd.append("referByEmail", this.auth.email);
      fd.append("referByMobile", this.auth.mobile ?? this.yycMobileHotline);
      fd.append("remarks", this.formData.remarks);
      fd.append("editMode", this.editMode);
      fd.append("byPassEmailBool", this.formData.byPassEmailBool);
      fd.append("companyAddress", this.formData.companyAddress);
      fd.append(
        "autoCaptureGrantApprovalEmail",
        this.formData.autoCaptureGrantApprovalEmail
      );
      fd.append("nextStageId", this.nextStageId);
      fd.append("programmeName", this.formData.programmeName);

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackError(() => {
        this.hideLoadingDialog();
        this.$store.dispatch("showMessage", {
          message: "Something went wrong. Please try again later.",
          messageType: "error",
          timeout: 2000,
        });
      });
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.hideLoadingDialog();
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.applicationNotFound) {
              this.$store.dispatch("showMessage", {
                message: response.data.applicationNo,
                messageType: "error",
                timeout: 2000,
              });
            }
          }
          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .email-page {
  background-color: var(--app-page);
}

.dark-mode .email-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark {
  background: var(--app-form-tile);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green {
  background: var(--app-form-tile);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green .v-icon {
  color: var(--app-text) !important;
}

.dark-mode .hero-action-btn--preview:hover {
  background: var(--app-accent-soft);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-text-muted);
}

.dark-mode .form-field >>> .v-chip .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .form-field >>> .v-radio .v-label {
  color: var(--app-text);
}

.dark-mode .email-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .email-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .email-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .email-confirm-card__btn--cancel {
  background: var(--app-form-tile);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .email-confirm-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card {
  background: var(--app-surface);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card__icon-wrap::after {
  border: 2px solid var(--app-border);
}

.dark-mode .file-card--has-file .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.dark-mode .file-card--empty .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.dark-mode .file-card--readonly .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
}

.dark-mode .file-card__name {
  color: var(--app-text);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .atch-card {
  border: 1.5px dashed var(--app-border-strong);
}

.dark-mode .atch-card:hover {
  border-color: var(--app-border-strong);
  background: var(--app-form-tile-hover);
}

.dark-mode .atch-card--has-file {
  background: var(--app-success-bg);
}

.dark-mode .atch-card--has-file:hover {
  background: var(--app-success-bg);
}

.dark-mode .atch-card__icon-wrap--red {
  background: var(--app-danger-bg);
}

.dark-mode .atch-card__icon-wrap--blue {
  background: var(--app-accent-soft);
}

.dark-mode .atch-card__icon-wrap--emerald {
  background: var(--app-success-bg);
}

.dark-mode .atch-card__preview-btn {
  background: var(--app-form-tile);
}

.dark-mode .atch-card__preview-btn:hover {
  background: var(--app-accent-soft);
}

.dark-mode .atch-card__label {
  color: var(--app-text);
}

.dark-mode .atch-card__sublabel {
  color: var(--app-text-faint);
}

.dark-mode .atch-card__input >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .atch-card--has-file .atch-card__input >>> .v-input__slot {
  background: var(--app-success-bg) !important;
}

.dark-mode .atch-readonly-label {
  color: var(--app-text-faint);
}

.dark-mode .atch-readonly-item {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .atch-readonly-item__icon {
  background: var(--app-form-tile);
}

.dark-mode .atch-readonly-item__icon svg {
  color: var(--app-text-faint);
}

.dark-mode .atch-readonly-item__name {
  color: var(--app-neutral-fg);
}

.dark-mode .atch-readonly-item__tag {
  background: var(--app-success-bg);
}

.dark-mode .toggle-card {
  border: 1.5px solid var(--app-border);
  background: var(--app-form-tile);
}

.dark-mode .toggle-card:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .toggle-card--active-blue {
  background: var(--app-accent-soft);
}

.dark-mode .toggle-card--active-red {
  background: var(--app-danger-bg);
}

.dark-mode .toggle-card__icon-wrap--blue {
  background: var(--app-accent-soft);
}

.dark-mode .toggle-card__icon-wrap--red {
  background: var(--app-danger-bg);
}

.dark-mode .toggle-card__title {
  color: var(--app-text);
}

.dark-mode .toggle-card__desc {
  color: var(--app-text-faint);
}

.dark-mode .toggle-card__check {
  border: 2px solid var(--app-border-strong);
}

.dark-mode .edit-select__trigger {
  border: 1.5px solid var(--app-field-border);
  background: var(--app-field);
}

.dark-mode .edit-select__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .edit-select__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .edit-select__mode-btn {
  background: var(--app-form-tile);
}

.dark-mode .edit-select__mode-btn:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .edit-select__mode-icon {
  color: var(--app-text-muted);
}

.dark-mode .edit-select__arrow {
  color: var(--app-text-faint);
}

.dark-mode .edit-select__dropdown {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .edit-select__dropdown::-webkit-scrollbar-thumb {
  background: var(--app-form-tile);
}

.dark-mode .edit-select__option {
  color: var(--app-text);
}

.dark-mode .edit-select__option:hover {
  background: var(--app-accent-soft);
}

.dark-mode .edit-select__option--selected {
  background: var(--app-accent-soft);
}

.dark-mode .edit-select__option--selected:hover {
  background: var(--app-accent-soft);
}

.dark-mode .edit-select__option-icon {
  color: var(--app-text-faint);
}

.dark-mode .edit-select__empty {
  color: var(--app-text-faint);
}

.dark-mode .smart-suggestion {
  background: var(--app-warning-bg);
}

.dark-mode .smart-suggestion__icon {
  background: var(--app-warning-bg);
}

.dark-mode .email-modal {
  background: var(--app-card);
}

.dark-mode .email-modal__header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .email-modal__header-left {
  color: var(--app-text);
}

.dark-mode .email-modal__close {
  color: var(--app-text-faint);
}

.dark-mode .email-modal__close:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-neutral-fg);
}

.dark-mode .email-modal__body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .email-preview__sheet {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
}

.dark-mode .email-preview__meta {
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .email-preview__meta-label {
  color: var(--app-text-muted);
}

.dark-mode .email-preview__meta-value {
  color: var(--app-text);
}

.dark-mode .email-preview__body {
  color: var(--app-text);
}

.dark-mode .email-preview__step {
  color: var(--app-text);
}

.dark-mode .email-preview__note {
  color: var(--app-text-muted);
}

.dark-mode .email-preview__signature {
  border-top: 1px solid var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .email-preview__signature strong {
  color: var(--app-text);
}

.dark-mode .email-preview__logo {
  border-top: 1px solid var(--app-border);
}

.dark-mode .email-preview__logo-placeholder {
  background: var(--app-form-tile);
  border: 1px dashed var(--app-border-strong);
}

.dark-mode .email-preview__logo-text {
  color: var(--app-neutral-fg);
}

.dark-mode .email-preview__logo-tagline {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.email-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   EMAIL HEADER
   ============================================ */
.email-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.email-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.email-header__info {
  flex: 1;
  min-width: 0;
}

.email-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.email-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
}

/* ============================================
   HERO ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

.hero-icon {
  width: 18px;
  height: 18px;
}

.hero-action-btn--dark {
  background: #f1f5f9;
  color: #1e293b;
}

.hero-action-btn--dark:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.hero-action-btn--green {
  background: #dcfce7;
  color: #16a34a;
}

.hero-action-btn--green:hover {
  background: #d1fae5;
  color: #059669;
}

.hero-action-btn--preview {
  width: auto;
  height: 38px;
  padding: 0 14px;
  gap: 6px;
  border-radius: 10px;
  background: #f0f7ff;
  color: #0369a1;
  border: 1.5px solid #bae6fd;
  flex-shrink: 0;
}

.hero-action-btn--preview:hover {
  background: #e0f2fe;
  color: #075985;
  border-color: #7dd3fc;
  transform: translateY(-1px);
}

.hero-action-btn__label {
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
}

/* ============================================
   CARDS GRID LAYOUT
   ============================================ */
.cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 960px) {
  .cards-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.cards-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
}

.section-divider {
  border: none;
  border-top: 1px solid #eceef1;
  margin: 12px 0;
}

/* ============================================
   FORM ROWS & FIELDS
   ============================================ */
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.form-row--attachment {
  align-items: center;
}

.form-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 10px;
}

.form-field {
  flex: 1;
  min-width: 0;
}

.form-field--inline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
}

.form-field--sub-col {
  flex: 1;
  min-width: 180px;
}

.form-field--file {
  flex: 1;
  min-width: 0;
}

/* Style Vuetify inputs to match design system */
.form-field >>> .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.form-field >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 40px !important;
}

.form-field >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  min-height: 0 !important;
}

.form-field >>> .v-messages {
  min-height: 0 !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
}

.form-field >>> .v-input--is-focused .v-input__slot {
  border-color: #56bde8 !important;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12) !important;
}

.form-field >>> .v-input__slot::before,
.form-field >>> .v-input__slot::after {
  display: none !important;
}

/* Editable fields - white background with visible border */
.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #d1d5db !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: #94a3b8 !important;
}

/* Disabled / read-only fields - flat muted look */
.form-field >>> .v-input--is-disabled .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px dashed #e2e8f0 !important;
  cursor: not-allowed !important;
}

.form-field >>> .v-input--is-disabled input,
.form-field >>> .v-input--is-disabled textarea,
.form-field >>> .v-input--is-disabled .v-select__selection {
  color: #94a3b8 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-focused) > .v-input__control > .v-input__slot::before {
  border: none !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot::after {
  border: none !important;
}

.form-field >>> .v-label {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  color: #64748b;
}

.form-field >>> .v-text-field input,
.form-field >>> .v-select__selection,
.form-field >>> .v-text-field textarea {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1e24;
}

/* Error state */
.form-field >>> .v-input--has-state .v-input__slot {
  border-color: #f87171 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled).v-input--has-state > .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
}

.form-field >>> .v-messages__message {
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  color: #ef4444;
}

/* Combobox chip styling */
.form-field >>> .v-chip {
  background: #f0f7ff !important;
  color: #0369a1 !important;
  border: 1px solid #bae6fd !important;
  border-radius: 6px !important;
  font-family: 'Lato', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
}

.form-field >>> .v-chip .v-icon {
  color: #64748b !important;
}

/* Radio group styling */
.form-field >>> .v-input--radio-group {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.form-field >>> .v-radio .v-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #334155;
}

/* ============================================
   APPLY GRANT HINT (non-scoped override)
   ============================================ */
.apply-grant-hint >>> .v-input__control .v-text-field__details .v-messages__message {
  color: red !important;
  font-style: italic;
  font-size: 14px;
}

/* ============================================
   EMAIL CONFIRM DIALOG
   ============================================ */
.email-confirm-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.email-confirm-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.email-confirm-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
}

.email-confirm-card__actions {
  display: flex;
  gap: 12px;
}

.email-confirm-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.email-confirm-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.email-confirm-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.email-confirm-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.email-confirm-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.v-btn {
  opacity: 1 !important;
}

.v-messages__message {
  color: #ef4444;
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .email-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  .email-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .email-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .email-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .email-header__actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  .hero-action-btn {
    width: 36px;
    height: 36px;
  }

  .hero-action-btn--preview {
    width: auto;
    height: 34px;
    padding: 0 10px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .cards-column {
    gap: 16px;
  }

  .section-card {
    border-radius: 14px;
    max-width: 100%;
    overflow: hidden;
  }

  .section-header {
    padding: 12px 16px;
  }

  .section-header__title {
    font-size: 0.65rem;
    letter-spacing: 1.5px;
  }

  .section-body {
    padding: 14px;
    overflow: hidden;
    word-break: break-word;
  }

  .form-row {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;
  }

  .form-row--attachment {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .form-label {
    flex: none;
    width: 100%;
    font-size: 0.72rem;
    padding-top: 0;
    margin-bottom: 2px;
  }

  .form-field {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .form-field >>> .v-input {
    width: 100% !important;
  }

  .form-field >>> .v-input__control {
    width: 100% !important;
  }

  .form-field >>> .v-input__slot {
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .form-field--inline {
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  .form-field--sub-col {
    min-width: 100%;
  }

  .cards-column {
    min-width: 0;
    max-width: 100%;
  }

  .email-page {
    overflow-x: hidden;
  }

  .email-confirm-card {
    padding: 24px 20px 20px;
  }

  .email-confirm-card__title {
    font-size: 1.05rem;
  }
}

/* Tablet breakpoint */
@media (min-width: 600px) and (max-width: 959px) {
  .email-page {
    zoom: 1;
  }

  .email-header__top {
    padding: 20px 20px 14px;
  }

  .email-header__title {
    font-size: 1.15rem;
  }

  .email-header__actions {
    flex-wrap: wrap;
    gap: 4px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .section-body {
    padding: 18px;
  }

  .form-label {
    flex: 0 0 160px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .email-page {
  background-color: var(--app-page);
}

.dark-mode .email-header {
  background: var(--app-card);
}

.dark-mode .email-header__title {
  color: var(--app-text);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .section-card {
  background: var(--app-card);
}

.dark-mode .section-header {
  background: linear-gradient(135deg, var(--app-accent), var(--app-accent));
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .section-divider {
  border-top-color: var(--app-border);
}

.dark-mode .form-label {
  color: var(--app-text-muted);
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-color: var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot:hover {
  background: var(--app-field-hover) !important;
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--app-accent) !important;
  box-shadow: 0 0 0 3px var(--app-ring) !important;
}

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text);
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-field-placeholder);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .hero-action-btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-neutral-fg);
}

.dark-mode .hero-action-btn--dark {
  background: var(--app-border);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark:hover {
  background: var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--preview {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
  border-color: var(--app-accent-border);
}

.dark-mode .hero-action-btn--preview:hover {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

.dark-mode .email-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .email-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .email-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .email-confirm-card__btn--cancel {
  background: var(--app-form-tile);
  color: var(--app-accent-strong);
  border-color: var(--app-form-tile-border);
}

.dark-mode .email-confirm-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-accent-strong);
}

.dark-mode .email-confirm-card__btn--confirm {
  background: var(--app-accent);
}

.dark-mode .email-confirm-card__btn--confirm:hover {
  background: var(--app-accent);
}

.dark-mode .form-field >>> .v-chip {
  background: var(--app-accent-soft) !important;
  color: var(--app-accent-strong) !important;
  border-color: var(--app-accent-border) !important;
}

.dark-mode .form-field >>> .v-chip .v-icon {
  color: var(--app-text-muted) !important;
}

/* ============================================
   FILE CARD (attachment style)
   ============================================ */
.file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.15s;
}

.file-card:hover {
  background: #f1f5f9;
}

.file-card--readonly {
  cursor: default;
}

.file-card__icon-wrap {
  position: relative;
  flex-shrink: 0;
}

.file-card__icon-wrap::after {
  content: '';
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #f8fafc;
  background-size: 10px;
  background-position: center;
  background-repeat: no-repeat;
}

.file-card--has-file .file-card__icon-wrap::after {
  background-color: #22c55e;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.file-card--empty .file-card__icon-wrap::after {
  background-color: #ef4444;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.file-card--readonly .file-card__icon-wrap::after {
  background-color: #22c55e;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.file-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-card__svg {
  width: 20px;
  height: 20px;
}

.file-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-card__name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-card__hint {
  font-size: 0.7rem;
  color: #94a3b8;
}

.file-card__hint--auto {
  color: #10b981;
  font-weight: 600;
}

.file-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.file-card__btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.file-card__btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.file-card__btn-icon {
  width: 16px;
  height: 16px;
}

.file-card__hidden {
  display: none;
}

.file-card__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #ef4444;
}

/* Dark mode file card */
.dark-mode .file-card {
  background: var(--app-form-tile);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .file-card__name {
  color: var(--app-neutral-fg);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__hint--auto {
  color: var(--app-success-fg);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-border);
  color: var(--app-neutral-fg);
}

.dark-mode .file-card__icon-wrap::after {
  border-color: var(--app-card);
}

/* OLD atch styles removed - now using file-card pattern */
.atch-grid-REMOVED_START {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.atch-card:last-child:nth-child(odd) {
  grid-column: 1;
}

.atch-card {
  border: 1.5px dashed #d1d5db;
  border-radius: 12px;
  padding: 16px;
  background: #fafbfc;
  transition: all 0.18s ease;
  position: relative;
  min-width: 0;
  overflow: hidden;
}

.atch-card:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.atch-card--has-file {
  border-style: solid;
  border-color: #10b981;
  background: #f0fdf4;
}

.atch-card--has-file:hover {
  border-color: #10b981;
  background: #ecfdf5;
}

.atch-card--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.atch-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.atch-card__icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.atch-card__icon-wrap--red {
  background: #fef2f2;
  color: #ef4444;
}

.atch-card__icon-wrap--blue {
  background: #eff6ff;
  color: #3b82f6;
}

.atch-card__icon-wrap--emerald {
  background: #ecfdf5;
  color: #10b981;
}

.atch-card__icon-wrap {
  position: relative;
}

.atch-card__icon {
  width: 20px;
  height: 20px;
}

.atch-card__tick {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #f0fdf4;
}

.atch-card__tick svg {
  width: 11px;
  height: 11px;
}

.atch-card__cross {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fafbfc;
}

.atch-card__cross svg {
  width: 10px;
  height: 10px;
}

.atch-card__preview-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 7px;
  border: 1px solid #bae6fd;
  background: #fff;
  color: #0369a1;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.atch-card__preview-btn:hover {
  background: #e0f2fe;
  border-color: #7dd3fc;
}

.atch-card__preview-icon {
  width: 14px;
  height: 14px;
}

.atch-card__label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e293b;
  font-family: 'Lato', sans-serif;
  line-height: 1.3;
}

.atch-card__sublabel {
  font-size: 0.7rem;
  color: #94a3b8;
  font-family: 'Lato', sans-serif;
  margin-bottom: 10px;
}

.atch-card__input >>> .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.atch-card__input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 36px !important;
  background: #fff !important;
  border: 1.5px solid #e2e8f0 !important;
}

.atch-card--has-file .atch-card__input >>> .v-input__slot {
  border-color: #a7f3d0 !important;
  background: #f0fdf4 !important;
}

.atch-card__input >>> .v-input__slot::before,
.atch-card__input >>> .v-input__slot::after {
  display: none !important;
}

.atch-card__input >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  min-height: 0 !important;
}

.atch-card__input >>> .v-messages {
  min-height: 0 !important;
}

.atch-card__input >>> .v-label {
  font-size: 0.78rem;
}

.atch-card__input >>> .v-text-field input {
  font-size: 0.78rem;
}

.atch-card__input {
  overflow: hidden;
}

.atch-card__input >>> .v-input {
  max-width: 100%;
}

.atch-card__input >>> .v-input__control {
  min-width: 0;
  max-width: 100%;
}

.atch-card__input >>> .v-input__slot {
  overflow: hidden !important;
  max-width: 100% !important;
  align-items: center !important;
}

.atch-card__input >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.atch-card__input >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.atch-card__input >>> .v-input__prepend-inner .v-icon {
  color: #ef4444 !important;
  font-size: 20px !important;
}

.atch-card__input >>> .v-file-input__text {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  min-width: 0 !important;
  max-width: 100% !important;
}

/* Read-only auto-included files */
.atch-readonly-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.atch-readonly-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.atch-readonly-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}

.atch-readonly-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.atch-readonly-item__icon svg {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}

.atch-readonly-item__info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.atch-readonly-item__name {
  font-size: 0.78rem;
  font-weight: 500;
  color: #475569;
  font-family: 'Lato', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.atch-readonly-item__tag {
  font-size: 0.65rem;
  font-weight: 600;
  color: #10b981;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Dark mode - attachment cards */
.dark-mode .atch-card {
  border-color: var(--app-border);
  background: var(--app-form-tile);
}

.dark-mode .atch-card:hover {
  border-color: var(--app-border-strong);
  background: var(--app-form-tile-hover);
}

.dark-mode .atch-card--has-file {
  border-color: var(--app-success-fg);
  background: var(--app-success-bg);
}

.dark-mode .atch-card--has-file:hover {
  border-color: var(--app-success-fg);
  background: var(--app-success);
}

.dark-mode .atch-card__icon-wrap--red {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .atch-card__icon-wrap--blue {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .atch-card__icon-wrap--emerald {
  background: var(--app-success-bg);
  color: var(--app-success-fg);
}

.dark-mode .atch-card__tick {
  box-shadow: 0 0 0 2px var(--app-success-bg);
}

.dark-mode .atch-card__cross {
  box-shadow: 0 0 0 2px var(--app-card);
}

.dark-mode .atch-card__label {
  color: var(--app-text);
}

.dark-mode .atch-card__sublabel {
  color: var(--app-text-faint);
}

.dark-mode .atch-card__preview-btn {
  background: var(--app-form-tile);
  border-color: var(--app-accent-border);
  color: var(--app-accent-strong);
}

.dark-mode .atch-card__preview-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-accent);
}

.dark-mode .atch-card__input >>> .v-input__slot {
  background: var(--app-field) !important;
  border-color: var(--app-field-border) !important;
}

.dark-mode .atch-card--has-file .atch-card__input >>> .v-input__slot {
  border-color: var(--app-success-fg) !important;
  background: var(--app-success-bg) !important;
}

.dark-mode .atch-readonly-item {
  background: var(--app-form-tile);
  border-color: var(--app-border);
}

.dark-mode .atch-readonly-item__icon {
  background: var(--app-border);
}

.dark-mode .atch-readonly-item__icon svg {
  color: var(--app-text-faint);
}

.dark-mode .atch-readonly-item__name {
  color: var(--app-text-muted);
}

.dark-mode .atch-readonly-item__tag {
  background: var(--app-success-bg);
  border-color: var(--app-success-fg);
  color: var(--app-success-fg);
}

/* Responsive - attachment cards */
@media (max-width: 599px) {
  .atch-grid {
    grid-template-columns: 1fr;
  }

  .atch-card {
    padding: 14px;
  }

  .atch-card:last-child:nth-child(odd) {
    max-width: 100%;
  }

  .atch-readonly-item__info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .atch-grid {
    grid-template-columns: 1fr;
  }

  .atch-card:last-child:nth-child(odd) {
    max-width: 100%;
  }
}

/* ============================================
   TOGGLE CARD RADIO
   ============================================ */
.toggle-card-group {
  display: flex;
  gap: 10px;
}

.toggle-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
}

.toggle-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.toggle-card--active-blue {
  border-color: #0ea5e9;
  background: #f0f9ff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.toggle-card--active-blue:hover {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.toggle-card--active-red {
  border-color: #f87171;
  background: #fef2f2;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.1);
}

.toggle-card--active-red:hover {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}

.toggle-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.toggle-card__icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toggle-card__icon-wrap--blue {
  background: #e0f2fe;
  color: #0ea5e9;
}

.toggle-card--active-blue .toggle-card__icon-wrap--blue {
  background: #0ea5e9;
  color: #fff;
}

.toggle-card__icon-wrap--red {
  background: #fee2e2;
  color: #ef4444;
}

.toggle-card--active-red .toggle-card__icon-wrap--red {
  background: #ef4444;
  color: #fff;
}

.toggle-card__icon {
  width: 18px;
  height: 18px;
}

.toggle-card__content {
  flex: 1;
  min-width: 0;
}

.toggle-card__title {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e293b;
  font-family: 'Lato', sans-serif;
  line-height: 1.3;
}

.toggle-card__desc {
  display: block;
  font-size: 0.72rem;
  font-weight: 400;
  color: #94a3b8;
  font-family: 'Lato', sans-serif;
  line-height: 1.4;
  margin-top: 1px;
}

.toggle-card__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.toggle-card__check--active {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.toggle-card__check--red {
  background: #ef4444;
  border-color: #ef4444;
}

.toggle-card__check-icon {
  width: 13px;
  height: 13px;
  color: #fff;
}

/* Dark mode - toggle cards */
.dark-mode .toggle-card {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .toggle-card:hover {
  border-color: var(--app-border-strong);
  box-shadow: 0 2px 8px var(--app-shadow-soft);
}

.dark-mode .toggle-card--active-blue {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .toggle-card--active-red {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-fg);
  box-shadow: 0 0 0 3px var(--app-ring-danger);
}

.dark-mode .toggle-card__icon-wrap--blue {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .toggle-card--active-blue .toggle-card__icon-wrap--blue {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.dark-mode .toggle-card__icon-wrap--red {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .toggle-card--active-red .toggle-card__icon-wrap--red {
  background: var(--app-danger);
  color: var(--app-on-solid);
}

.dark-mode .toggle-card__title {
  color: var(--app-text);
}

.dark-mode .toggle-card__desc {
  color: var(--app-text-faint);
}

.dark-mode .toggle-card__check {
  border-color: var(--app-border-strong);
}

/* Responsive - toggle cards stack on mobile */
@media (max-width: 599px) {
  .toggle-card-group {
    flex-direction: column;
  }

  .toggle-card {
    padding: 12px 14px;
  }

  .toggle-card__icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .toggle-card__icon {
    width: 16px;
    height: 16px;
  }
}

/* ============================================
   CUSTOM DROPDOWN (edit-select)
   ============================================ */
.edit-select {
  position: relative;
}

.edit-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 6px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
}

.edit-select__trigger:hover {
  border-color: #94a3b8;
}

.edit-select__trigger--open {
  border-color: #56bde8;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12);
}

.edit-select__trigger--error {
  border-color: #ef4444;
}

.edit-select__value {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1e24;
}

.edit-select__placeholder {
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 400;
}

.edit-select__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  flex-shrink: 0;
}

.edit-select__mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #f1f5f9;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.edit-select__mode-btn:hover {
  background: #e2e8f0;
}

.edit-select__mode-icon {
  width: 16px;
  height: 16px;
  color: #64748b;
}

.edit-select__mode-btn:hover .edit-select__mode-icon {
  color: #0ea5e9;
}

.edit-select__arrow {
  width: 20px;
  height: 20px;
  color: #94a3b8;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.edit-select__arrow--open {
  transform: rotate(180deg);
}

.edit-select__dropdown {
  margin-top: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  z-index: 10;
  position: relative;
}

.edit-select__dropdown::-webkit-scrollbar {
  width: 5px;
}

.edit-select__dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.edit-select__dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.edit-select__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: background 0.12s;
}

.edit-select__option:hover {
  background: #e0f2fe;
}

.edit-select__option--selected {
  background: #f0f9ff;
  font-weight: 600;
  color: #0369a1;
}

.edit-select__option--selected:hover {
  background: #e0f2fe;
}

.edit-select__option-icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
  flex-shrink: 0;
}

.edit-select__option--selected .edit-select__option-icon {
  color: #0ea5e9;
}

.edit-select__empty {
  padding: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
}

.edit-select__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #ef4444;
  font-family: 'Lato', sans-serif;
}

.edit-select__error-item {
  padding-left: 12px;
  position: relative;
  line-height: 1.5;
}

.edit-select__error-item::before {
  content: '\2022';
  position: absolute;
  left: 0;
}

.edit-select__hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #ef4444;
  font-style: italic;
  font-family: 'Lato', sans-serif;
}

/* Smart Suggestion banner (apply-grant deadline lands on a public holiday) */
.smart-suggestion {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  background: #fffbeb;
  font-family: 'Lato', sans-serif;
}
.smart-suggestion__icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  color: #d97706;
}
.smart-suggestion__icon svg {
  width: 18px;
  height: 18px;
}
.smart-suggestion__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.smart-suggestion__title {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #b45309;
}
.smart-suggestion__text {
  font-size: 0.82rem;
  line-height: 1.45;
  color: #78350f;
}
.smart-suggestion__text strong {
  font-weight: 700;
  color: #92400e;
}
.dark-mode .smart-suggestion {
  background: var(--app-warning-bg);
  border-color: var(--app-warning-fg);
}
.dark-mode .smart-suggestion__icon {
  background: var(--app-warning-bg-strong);
  color: var(--app-warning-fg);
}
.dark-mode .smart-suggestion__title {
  color: var(--app-warning-fg);
}
.dark-mode .smart-suggestion__text {
  color: var(--app-warning-fg);
}
.dark-mode .smart-suggestion__text strong {
  color: var(--app-warning-fg);
}

/* Dark mode - custom dropdown */
.dark-mode .edit-select__trigger {
  background: var(--app-field);
  border-color: var(--app-field-border);
  color: var(--app-text);
}

.dark-mode .edit-select__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .edit-select__trigger--open {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .edit-select__value {
  color: var(--app-text);
}

.dark-mode .edit-select__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .edit-select__mode-btn {
  background: var(--app-form-tile);
}

.dark-mode .edit-select__mode-btn:hover {
  background: var(--app-border);
}

.dark-mode .edit-select__mode-icon {
  color: var(--app-text-muted);
}

.dark-mode .edit-select__mode-btn:hover .edit-select__mode-icon {
  color: var(--app-accent);
}

.dark-mode .edit-select__dropdown {
  background: var(--app-form-tile);
  border-color: var(--app-border);
  box-shadow: 0 8px 24px var(--app-shadow-mid);
}

.dark-mode .edit-select__option {
  color: var(--app-neutral-fg);
}

.dark-mode .edit-select__option:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .edit-select__option--selected {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

.dark-mode .edit-select__option--selected:hover {
  background: var(--app-accent-soft);
}

.dark-mode .edit-select__option-icon {
  color: var(--app-text-faint);
}

.dark-mode .edit-select__option--selected .edit-select__option-icon {
  color: var(--app-accent);
}

/* ──────────────────────────────────────────────
   Email Preview Section
   ────────────────────────────────────────────── */
/* ============================================
   EMAIL PREVIEW FAB
   ============================================ */
.preview-fab {
  position: fixed !important;
  bottom: 28px !important;
  right: 28px !important;
  left: auto !important;
  top: auto !important;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #1e293b;
  color: #fff;
  border: none;
  border-radius: 14px;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(30, 41, 59, 0.35);
  transition: background 0.2s ease, box-shadow 0.2s ease;
  z-index: 9999;
  overflow: hidden;
}

.preview-fab:hover {
  background: #334155;
  box-shadow: 0 6px 20px rgba(30, 41, 59, 0.45);
  transform: translateY(-2px);
}

.preview-fab__icon {
  width: 18px;
  height: 18px;
}

.preview-fab__label {
  white-space: nowrap;
}

/* Shine effect */
.preview-fab--shine {
  position: relative;
}

.preview-fab--shine::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    105deg,
    transparent 40%,
    rgba(255, 255, 255, 0.25) 45%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0.25) 55%,
    transparent 60%
  );
  border-radius: inherit;
  animation: fab-shine 3s infinite;
  pointer-events: none;
}

@keyframes fab-shine {
  0% { left: -100%; }
  30% { left: 150%; }
  100% { left: 150%; }
}

/* NEW badge */
.preview-fab__new-badge {
  background: #ef4444;
  color: #fff;
  font-size: 0.55rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 6px;
  letter-spacing: 0.8px;
  animation: badge-pulse 2s infinite;
  line-height: 1.3;
}

@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* ============================================
   EMAIL PREVIEW MODAL
   ============================================ */
.email-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.email-modal {
  width: 960px;
  max-width: 94vw;
  max-height: 88vh;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Lato', sans-serif;
}

.email-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
}

.email-modal__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.email-modal__header-icon {
  width: 22px;
  height: 22px;
  color: #0ea5e9;
}

.email-modal__close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: all 0.15s;
}

.email-modal__close:hover {
  background: #f1f5f9;
  color: #475569;
}

.email-modal__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #eef1f5;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.email-modal__body::-webkit-scrollbar {
  width: 5px;
}

.email-modal__body::-webkit-scrollbar-track {
  background: transparent;
}

.email-modal__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

/* Modal transition — fade backdrop + scale the dialog in/out */
.modal-enter-active,
.modal-leave-active {
  transition: background 0.25s ease;
}

.modal-enter-active .email-modal,
.modal-leave-active .email-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-enter .email-modal,
.modal-leave-to .email-modal {
  transform: scale(0.95);
  opacity: 0;
}

.modal-enter,
.modal-leave-to {
  background: rgba(15, 23, 42, 0);
}

/* Email content sits on a white "sheet" over the modal's light backdrop. */
.email-preview__content {
  padding: 20px;
}

.email-preview__sheet {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 10px 30px rgba(15, 23, 42, 0.08);
}

.email-preview__meta {
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.email-preview__meta-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.78rem;
  line-height: 1.5;
}

.email-preview__meta-label {
  font-weight: 600;
  color: #64748b;
  min-width: 52px;
  flex-shrink: 0;
}

.email-preview__meta-value {
  color: #334155;
  word-break: break-all;
}

.email-preview__body {
  padding: 28px 32px 36px;
  font-size: 0.85rem;
  line-height: 1.7;
  color: #334155;
}

.email-preview__body p {
  margin: 0 0 12px;
}

.email-preview__body a {
  color: #2563eb;
  text-decoration: underline;
}

.email-preview__step {
  font-weight: 700;
  text-decoration: underline;
  margin-top: 20px !important;
  margin-bottom: 8px !important;
  color: #1e293b;
}

.email-preview__highlight {
  background: #fef08a;
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 600;
}

.email-preview__list {
  margin: 0 0 12px;
  padding-left: 24px;
}

.email-preview__list li {
  margin-bottom: 6px;
}

.email-preview__list--alpha {
  list-style-type: lower-alpha;
  margin-top: 6px;
}

.email-preview__note {
  font-style: italic;
  color: #64748b;
  font-size: 0.8rem;
}

.email-preview__signature {
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.6;
}

.email-preview__signature p {
  margin: 0 0 2px;
}

.email-preview__signature strong {
  color: #334155;
}

.email-preview__logo {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.email-preview__logo-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 12px 28px;
  margin-bottom: 8px;
}

.email-preview__logo-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.5px;
}

.email-preview__logo-tagline {
  font-size: 0.75rem;
  color: #94a3b8;
  line-height: 1.5;
  max-width: 480px;
  margin: 0 auto;
  font-style: italic;
}

/* ── Dark mode ── */
.dark-mode .preview-fab {
  background: var(--app-border);
  box-shadow: 0 4px 14px var(--app-shadow);
}

.dark-mode .preview-fab:hover {
  background: var(--app-border-strong);
}

.dark-mode .email-modal-overlay {
  background: var(--app-overlay);
}

.dark-mode .email-modal {
  background: var(--app-card);
  box-shadow: 0 24px 60px var(--app-shadow);
}

.dark-mode .email-modal__header {
  border-bottom-color: var(--app-border);
}

.dark-mode .email-modal__header-left {
  color: var(--app-text);
}

.dark-mode .email-modal__close {
  color: var(--app-text-faint);
}

.dark-mode .email-modal__close:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text-muted);
}

.dark-mode .email-modal__body {
  background: var(--app-card);
}

.dark-mode .email-preview__sheet {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
  box-shadow: 0 10px 30px var(--app-shadow);
}

.dark-mode .email-preview {
  background: var(--app-form-tile);
}

.dark-mode .email-preview__meta {
  background: var(--app-form-tile);
  border-bottom-color: var(--app-border);
}

.dark-mode .email-preview__meta-label {
  color: var(--app-text-muted);
}

.dark-mode .email-preview__meta-value {
  color: var(--app-text);
}

.dark-mode .email-preview__body {
  color: var(--app-neutral-fg);
}

.dark-mode .email-preview__body a {
  color: var(--app-accent);
}

.dark-mode .email-preview__step {
  color: var(--app-text);
}

.dark-mode .email-preview__highlight {
  background: var(--app-warning-bg);
  color: var(--app-warning-fg);
}

.dark-mode .email-preview__note {
  color: var(--app-text-muted);
}

.dark-mode .email-preview__signature {
  border-top-color: var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .email-preview__signature strong {
  color: var(--app-text);
}

.dark-mode .email-preview__logo {
  border-top-color: var(--app-border);
}

.dark-mode .email-preview__logo-placeholder {
  background: var(--app-form-tile);
  border-color: var(--app-border-strong);
}

.dark-mode .email-preview__logo-text {
  color: var(--app-text-muted);
}

.dark-mode .email-preview__logo-tagline {
  color: var(--app-text-faint);
}

/* ── Mobile responsive ── */
@media (max-width: 600px) {
  .email-preview__body {
    padding: 20px 16px 28px;
    font-size: 0.8rem;
  }

  .email-preview__meta {
    padding: 12px 16px;
  }

  .email-preview__meta-row {
    font-size: 0.74rem;
  }

  .email-preview__signature {
    font-size: 0.78rem;
  }

  .email-preview__content {
    padding: 12px;
  }

  .preview-fab {
    bottom: 16px;
    right: 16px;
    padding: 10px 16px;
    border-radius: 12px;
    font-size: 0.8rem;
  }

  .email-modal-overlay {
    padding: 12px;
  }

  .email-modal {
    width: 100%;
    max-width: 100vw;
    max-height: 92vh;
  }
}

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text) !important;
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-header .v-btn:hover {
  background: var(--app-form-tile-hover) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-date-picker-table .v-btn--disabled {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-form-tile) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* HRDC datepicker theme — kept in sync with enrollment_detail / revert.
   Unscoped on purpose: the v-date-picker popup is teleported outside this
   component by v-menu, so scoped styles can't reach it. */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: 'Lato', sans-serif !important;
}

.v-picker .v-picker__body {
  border: none !important;
}

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

.v-picker__body {
  background: #fff !important;
}

.v-date-picker-header {
  padding: 12px 16px 8px !important;
  background: #fff !important;
}

.v-date-picker-header__value button {
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #1e293b !important;
}

.v-date-picker-header .v-btn {
  color: #64748b !important;
}

.v-date-picker-header .v-btn:hover {
  background: #f1f5f9 !important;
}

.v-date-picker-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  padding: 8px 0 !important;
}

.v-date-picker-table .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}

.v-date-picker-table .v-btn:hover {
  background: #f0f9ff !important;
}

.v-date-picker-table .v-btn--active {
  background: #0ea5e9 !important;
  color: #fff !important;
  font-weight: 700 !important;
}

.v-date-picker-table .v-btn--active:hover {
  background: #0284c7 !important;
}

.v-date-picker-table .v-btn--outlined {
  border-color: #0ea5e9 !important;
  color: #0ea5e9 !important;
}

.v-date-picker-table .v-btn--disabled {
  color: #cbd5e1 !important;
}

.v-picker__actions {
  padding: 8px 16px 14px !important;
  background: #fff !important;
  border-top: 1px solid #f1f5f9 !important;
}

.v-picker__actions .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
}

.v-date-picker-years li,
.v-date-picker-months .v-btn {
  font-family: 'Lato', sans-serif !important;
}
</style>
